import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles, mergeClasses, tokens, Button } from '@fluentui/react-components';
import { Heading, Container, Breadcrumb } from '../theme/primitives';
import { iconMap } from '../theme/icons';
import { stonePalette } from '../theme/brand';
import workshopImg from '../assets/img/marketing/about-workshop.jpg';

const ViewInArIcon = iconMap.view_in_ar;

const VALUES = [
  { icon: 'workspace_premium', titleKey: 'about.0_title', textKey: 'about.0_text' },
  { icon: 'handyman', titleKey: 'about.1_title', textKey: 'about.1_text' },
  { icon: 'lightbulb', titleKey: 'about.2_title', textKey: 'about.2_text' },
  { icon: 'groups', titleKey: 'about.3_title', textKey: 'about.3_text' },
];

const STATS = [
  { valueKey: 'about.stats_experience_v', labelKey: 'about.stats_experience_l' },
  { valueKey: 'about.stats_clients_v', labelKey: 'about.stats_clients_l' },
  { valueKey: 'about.stats_time_v', labelKey: 'about.stats_time_l' },
  { valueKey: 'about.stats_made_v', labelKey: 'about.stats_made_l' },
];

const HIGHLIGHTS = [
  { icon: 'star', labelKey: 'about.highlight_0_label', subKey: 'about.highlight_0_sub' },
  { icon: 'local_shipping', labelKey: 'about.highlight_1_label', subKey: 'about.highlight_1_sub' },
  { icon: 'precision_manufacturing', labelKey: 'about.highlight_2_label', subKey: 'about.highlight_2_sub' },
  { icon: 'support_agent', labelKey: 'about.highlight_3_label', subKey: 'about.highlight_3_sub' },
];

const useStyles = makeStyles({
  titleBar: {
    paddingTop: '2.5rem', paddingBottom: '2.5rem',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  sectionPadX: {
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },

  // ── stats juosta ─────────────────────────────────────────────────────────
  statsSection: { borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  statsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
    '@media (min-width: 1024px)': { gridTemplateColumns: 'repeat(4, 1fr)' },
  },
  statCell: {
    paddingTop: '2.5rem', paddingBottom: '2.5rem',
    paddingLeft: tokens.spacingHorizontalXL, paddingRight: tokens.spacingHorizontalXL,
    textAlign: 'center',
  },
  statCellDivideTop: {
    borderTopWidth: '1px', borderTopStyle: 'solid', borderTopColor: tokens.colorNeutralStroke2,
    '@media (min-width: 1024px)': { borderTopWidth: 0 },
  },
  statCellDivideLeft: {
    '@media (min-width: 1024px)': { borderLeftWidth: '1px', borderLeftStyle: 'solid', borderLeftColor: tokens.colorNeutralStroke2 },
  },
  statValue: { fontSize: '1.875rem', fontWeight: tokens.fontWeightBold, color: tokens.colorBrandForeground2, marginBottom: tokens.spacingVerticalXXS },
  statLabel: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2 },

  // ── istorija ──────────────────────────────────────────────────────────────
  storySection: { paddingTop: '6rem', paddingBottom: '6rem' },
  storyGrid: {
    display: 'grid', gridTemplateColumns: '1fr', gap: '4rem', alignItems: 'center',
    '@media (min-width: 1024px)': { gridTemplateColumns: 'repeat(2, 1fr)', gap: '5rem' },
  },
  eyebrow: { display: 'block', marginBottom: tokens.spacingVerticalM },
  storyTitle: { marginBottom: tokens.spacingVerticalL },
  storyDivider: { width: '3rem', height: '2px', backgroundColor: tokens.colorBrandBackground, marginBottom: tokens.spacingVerticalXXL, border: 'none' },
  storyText: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, fontSize: tokens.fontSizeBase400, color: tokens.colorNeutralForeground2, lineHeight: '1.6' },
  storyParagraph: { margin: 0 },
  storyActions: { marginTop: tokens.spacingVerticalXXL, display: 'flex', flexDirection: 'column', gap: tokens.spacingHorizontalM, '@media (min-width: 640px)': { flexDirection: 'row' } },

  storyImageWrap: {
    borderRadius: tokens.borderRadiusXLarge, overflow: 'hidden', boxShadow: tokens.shadow16,
    aspectRatio: '4 / 3', marginBottom: tokens.spacingVerticalXL,
  },
  storyImage: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  highlightsGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: tokens.spacingHorizontalL },
  highlightCard: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  highlightIconChip: {
    width: '40px', height: '40px', borderRadius: tokens.borderRadiusLarge, flexShrink: 0,
    backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  highlightLabel: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, fontSize: tokens.fontSizeBase300 },
  highlightSub: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginTop: '2px' },

  // ── vertybės ──────────────────────────────────────────────────────────────
  valuesSection: { paddingTop: '6rem', paddingBottom: '6rem', backgroundColor: stonePalette[50] },
  sectionHeaderCenter: { textAlign: 'center', marginBottom: '4rem' },
  sectionSub: { maxWidth: '32rem', marginLeft: 'auto', marginRight: 'auto', marginTop: tokens.spacingVerticalS },
  valuesGrid: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalL,
    '@media (min-width: 640px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
    '@media (min-width: 1024px)': { gridTemplateColumns: 'repeat(4, 1fr)' },
  },
  valueCard: {
    backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusXLarge,
    padding: '2rem', textAlign: 'center',
    transitionProperty: 'box-shadow, border-color, transform', transitionDuration: tokens.durationNormal,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    ':hover': { boxShadow: tokens.shadow8, borderColor: tokens.colorNeutralStroke1, transform: 'translateY(-2px)' },
  },
  valueIconChip: {
    width: '56px', height: '56px', borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginLeft: 'auto', marginRight: 'auto', marginBottom: tokens.spacingVerticalL,
    transitionProperty: 'background-color', transitionDuration: tokens.durationNormal,
  },
  valueTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, marginBottom: tokens.spacingVerticalXS },
  valueText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, lineHeight: '1.6', margin: 0 },

  // ── CTA ───────────────────────────────────────────────────────────────────
  ctaSection: { paddingTop: '5rem', paddingBottom: '5rem', backgroundColor: tokens.colorBrandBackground },
  ctaInner: { textAlign: 'center' },
  ctaTitle: { fontSize: '1.875rem', fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForegroundOnBrand, marginBottom: tokens.spacingVerticalM },
  ctaSubtitle: { color: tokens.colorNeutralForegroundOnBrand, opacity: 0.85, fontSize: tokens.fontSizeBase400, marginBottom: tokens.spacingVerticalXXL },
  ctaButtons: { display: 'flex', flexDirection: 'column', gap: tokens.spacingHorizontalM, justifyContent: 'center', '@media (min-width: 640px)': { flexDirection: 'row' } },
  // Standartinis Fluent <Button> (aukštis/paddingas/šriftas/kampai — iš paties
  // komponento), tik spalvos perrašomos, kad skaitytųsi ant žalio CTA fono
  // (Fluent neturi atskiro "on-brand" appearance varianto).
  ctaBtnWhite: {
    backgroundColor: tokens.colorNeutralBackground1, color: tokens.colorBrandForeground2, border: 'none',
    ':hover': { backgroundColor: tokens.colorNeutralBackground2, color: tokens.colorBrandForeground2 },
  },
  ctaBtnOutline: {
    backgroundColor: 'transparent', color: tokens.colorNeutralForegroundOnBrand, border: '1px solid rgba(255,255,255,0.4)',
    ':hover': { backgroundColor: tokens.colorBrandBackgroundHover, color: tokens.colorNeutralForegroundOnBrand },
  },
});

function About() {
  const s = useStyles();
  const { t } = useTranslation();
  useEffect(() => { document.title = `${t('about.eyebrow')} | Fitit`; }, [t]);
  return (
    <div>

      {/* Page Title */}
      <div className={s.titleBar}>
        <Container size="xl">
          <Breadcrumb items={[{ label: t('furniture_list.breadcrumb_home'), to: '/' }, { label: t('about.eyebrow') }]} />
          <Heading as="h1" variant="h1">{t('about.page_heading')}</Heading>
        </Container>
      </div>

      {/* Stats strip */}
      <section className={s.statsSection}>
        <Container size="xl" className={s.sectionPadX}>
          <div className={s.statsGrid}>
            {STATS.map(({ valueKey, labelKey }, idx) => (
              <div key={labelKey} className={mergeClasses(s.statCell, idx >= 2 && s.statCellDivideTop, idx > 0 && s.statCellDivideLeft)}>
                <div className={s.statValue}>{t(valueKey)}</div>
                <div className={s.statLabel}>{t(labelKey)}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Story */}
      <section className={mergeClasses(s.storySection, s.sectionPadX)}>
        <Container size="xl">
          <div className={s.storyGrid}>
            <div>
              <Heading as="span" variant="eyebrow" className={s.eyebrow}>{t('about.story_eyebrow')}</Heading>
              <Heading as="h2" variant="h2" className={s.storyTitle}>{t('about.story_title')}</Heading>
              <hr className={s.storyDivider} />
              <div className={s.storyText}>
                <p className={s.storyParagraph}>{t('about.story_p1')}</p>
                <p className={s.storyParagraph}>{t('about.story_p2')}</p>
                <p className={s.storyParagraph}>{t('about.story_p3')}</p>
              </div>
              <div className={s.storyActions}>
                <Button appearance="primary" size="large" as={Link} to="/konfiguratorius" icon={<ViewInArIcon fontSize={18} />}>
                  {t('about.try_configurator')}
                </Button>
                <Button appearance="outline" size="large" as={Link} to="/siusti-uzklausa">
                  {t('about.contact_us')}
                </Button>
              </div>
            </div>

            <div>
              <div className={s.storyImageWrap}>
                <img src={workshopImg} alt={t('about.workshop_img_alt')} className={s.storyImage} />
              </div>
              <div className={s.highlightsGrid}>
                {HIGHLIGHTS.map(({ icon, labelKey, subKey }) => {
                  const Icon = iconMap[icon];
                  return (
                    <div key={labelKey} className={s.highlightCard}>
                      <div className={s.highlightIconChip}>
                        {Icon && <Icon fontSize={20} />}
                      </div>
                      <div>
                        <div className={s.highlightLabel}>{t(labelKey)}</div>
                        <div className={s.highlightSub}>{t(subKey)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Values */}
      <section className={mergeClasses(s.valuesSection, s.sectionPadX)}>
        <Container size="xl">
          <div className={s.sectionHeaderCenter}>
            <Heading as="span" variant="eyebrow" className={s.eyebrow}>{t('about.principles_eyebrow')}</Heading>
            <Heading as="h2" variant="section">{t('about.values_title')}</Heading>
            <Heading as="p" variant="sectionSub" className={s.sectionSub}>{t('about.values_subtitle')}</Heading>
          </div>
          <div className={s.valuesGrid}>
            {VALUES.map(({ icon, titleKey, textKey }) => {
              const Icon = iconMap[icon];
              return (
                <div key={titleKey} className={s.valueCard}>
                  <div className={s.valueIconChip}>
                    {Icon && <Icon fontSize={28} />}
                  </div>
                  <div className={s.valueTitle}>{t(titleKey)}</div>
                  <p className={s.valueText}>{t(textKey)}</p>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className={mergeClasses(s.ctaSection, s.sectionPadX)}>
        <Container size="md" className={s.ctaInner}>
          <h2 className={s.ctaTitle}>{t('about.cta_title')}</h2>
          <p className={s.ctaSubtitle}>
            {t('about.cta_subtitle')}
          </p>
          <div className={s.ctaButtons}>
            <Button appearance="primary" size="large" as={Link} to="/konfiguratorius" icon={<ViewInArIcon fontSize={20} />} className={s.ctaBtnWhite}>
              {t('about.cta_configure')}
            </Button>
            <Button appearance="outline" size="large" as={Link} to="/siusti-uzklausa" className={s.ctaBtnOutline}>
              {t('about.cta_inquiry')}
            </Button>
          </div>
        </Container>
      </section>

    </div>
  );
}

export default About;
